import { ANY_FUNCTION, cardLineFields, compileModifier, valuesIn, type Field, type FieldNode, type FilterItem, type LayoutNode, type ListCalendar, type ListCard, type ListGantt, type ListMap, type MapNode, type MenuItem, type Page, type SectionNode, type SheetNode, type StepNode, type TabNode, type TabsNode } from '@fieldia/core';
import type { DesignerWords } from './designer-words';
import { en } from './locales/en';
import { headFields, headParts, tabCountFields } from './head-parts';
import { original } from './draft';

/**
 * Finding one's way around a page's layout, for the designer's edits: the
 * containers that hold fields, a node and where it sits, a tab and its tabs.
 */

export type Container = { id: string; children: LayoutNode[] };

/** Every container that holds fields: steps, sections, tabs, and a page of sections or a sheet itself. */
export function containers(page: Page): Container[] {
  const found: Container[] = [];
  const walk = (nodes: LayoutNode[]) => {
    for (const node of nodes) {
      if (node.type === 'section') {
        found.push(node);
        walk(node.children);
      } else if (node.type === 'tabs') {
        for (const tab of node.children) {
          found.push(tab);
          walk(tab.children);
        }
      }
    }
  };
  const root = page.layout;
  if (root.type === 'wizard') {
    for (const step of root.children) {
      found.push(step);
      walk(step.children);
    }
  } else if (root.type === 'tabs') {
    for (const tab of root.children) {
      found.push(tab);
      walk(tab.children);
    }
  } else if (root.type !== 'list') {
    if (root.type === 'sections' || root.type === 'sheet') found.push(root);
    walk(root.children);
  }
  return found;
}

/** Where each container of a page sits in it, by the keys from its layout, and each node's container and place: once a page. */
const placesOf = new WeakMap<Page, { at: Map<string, { parent: Container; index: number }>; paths: Map<Container, (string | number)[]> }>();
function places(page: Page) {
  let found = placesOf.get(page);
  if (found) return found;
  const at = new Map<string, { parent: Container; index: number }>();
  for (const parent of containers(page)) parent.children.forEach((child, index) => at.has(child.id) || at.set(child.id, { parent, index }));
  const paths = new Map<Container, (string | number)[]>();
  const walk = (value: unknown, path: (string | number)[]) => {
    if (!value || typeof value !== 'object') return;
    if (Array.isArray(value)) return value.forEach((one, i) => walk(one, [...path, i]));
    const part = value as Record<string, unknown>;
    if (Array.isArray(part['children'])) paths.set(part as unknown as Container, path);
    for (const [key, one] of Object.entries(part)) if (one && typeof one === 'object') walk(one, [...path, key]);
  };
  walk(page.layout, ['layout']);
  placesOf.set(page, (found = { at, paths }));
  return found;
}

export function findNode(page: Page, id: string): { node: LayoutNode; parent: Container; index: number } | null {
  // A draft (draft.ts): found where it sat in the page the draft was made from, and read there in the draft alone —
  // never the whole layout read through the draft. Moved since, it is looked for as on any page.
  const base = original(page);
  if (base) {
    const { at, paths } = places(base);
    const was = at.get(id);
    const path = was && paths.get(was.parent);
    if (was && path) {
      const parent = path.reduce<unknown>((part, key) => (part && typeof part === 'object' ? (part as Record<string | number, unknown>)[key] : undefined), page) as Container | undefined;
      const node = parent?.children?.[was.index];
      if (node?.id === id) return { node, parent: parent as Container, index: was.index };
    }
  }
  for (const parent of containers(page)) {
    const index = parent.children.findIndex((child) => child.id === id);
    if (index !== -1) return { node: parent.children[index], parent, index };
  }
  return null;
}

export function findContainer(page: Page, id: string): Container | (StepNode | SectionNode | TabNode) | null {
  return containers(page).find((c) => c.id === id) ?? null;
}

/** Every tabs node, wherever it sits. */
export function tabsNodes(page: Page): TabsNode[] {
  const found: TabsNode[] = [];
  const root = page.layout;
  if (root.type === 'tabs') found.push(root);
  for (const container of containers(page)) for (const child of container.children) if (child.type === 'tabs') found.push(child);
  return found;
}

/** A tab, the tabs it belongs to, and its place among them. */
export function findTab(page: Page, id: string): { tab: TabNode; tabs: TabsNode; index: number } | null {
  for (const tabs of tabsNodes(page)) {
    const index = tabs.children.findIndex((tab) => tab.id === id);
    if (index !== -1) return { tab: tabs.children[index], tabs, index };
  }
  return null;
}

/** The first section, wherever it sits: where a field with nowhere else to go is put. */
export function firstSection(page: Page): SectionNode | null {
  return (containers(page).find((c) => (c as SectionNode).type === 'section') as SectionNode | undefined) ?? null;
}

export function nextName(taken: (name: string) => boolean, prefix: string, sep: string): string {
  for (let n = 1; ; n++) if (!taken(`${prefix}${sep}${n}`)) return `${prefix}${sep}${n}`;
}

export function allIds(page: Page): Set<string> {
  const ids = new Set<string>([page.layout.id]);
  for (const c of containers(page)) {
    ids.add(c.id);
    for (const child of c.children) ids.add(child.id);
  }
  // A sheet's header parts have ids of their own.
  const root = page.layout;
  if (root.type === 'sheet') for (const part of sheetParts(root)) ids.add(part.id);
  // A menu's items too, at any depth: a button's menu's, the gear menu's, and the menus their items open.
  if (root.type === 'sheet') for (const part of sheetParts(root)) for (const id of menuIds((part as { menu?: MenuItem[] }).menu)) ids.add(id);
  for (const c of containers(page)) for (const child of c.children) if (child.type === 'button') for (const id of menuIds(child.menu)) ids.add(id);
  // A list's filters and buttons too, a row's own among them; and a related list's rows' buttons.
  if (root.type === 'list') for (const part of [...(root.filters ?? []), ...(root.actions ?? []), ...(root.rowButtons ?? [])]) ids.add(part.id);
  for (const c of containers(page)) for (const child of c.children) if (child.type === 'related') for (const button of child.rowButtons ?? []) ids.add(button.id);
  return ids;
}

/** The fields the page shows somewhere: in its containers, and in a sheet's title, statusbar and stat buttons. */
export function shownFields(page: Page): Set<string> {
  const shown = new Set(containers(page).flatMap((c) => c.children.filter((n): n is FieldNode => n.type === 'field').map((n) => n.field)));
  // Words that show a field's value, among the parts.
  for (const c of containers(page)) for (const n of c.children) if (n.type === 'text') for (const name of valuesIn(n.text)) shown.add(name);
  // A map names the record's fields: its places, its pins' and cards', a layer's lines, where a route's numbers go.
  for (const c of containers(page)) for (const n of c.children) if (n.type === 'map') for (const name of mapNodeFields(n)) shown.add(name);
  // A field's widget that shows another of the record's fields beside its own: a date range's end (`endField`).
  for (const c of containers(page)) {
    for (const n of c.children) {
      if (n.type !== 'field') continue;
      for (const [key, value] of Object.entries(n.options ?? {})) if (key.endsWith('Field') && typeof value === 'string' && page.fields[value]) shown.add(value);
    }
  }
  const root = page.layout;
  if (root.type === 'sheet') {
    const title = root.title;
    const bar = root.statusbar as { field: string; durationsField?: string } | undefined;
    for (const name of [title?.field, title?.subtitleField, title?.avatarField, bar?.field, bar?.durationsField]) if (name) shown.add(name);
    // Stat buttons, ribbons and alerts show fields too: a value, a unit, words from a field, a value inside words.
    for (const part of sheetParts(root) as Record<string, unknown>[]) {
      for (const [key, value] of Object.entries(part)) if (typeof value === 'string' && (key === 'field' || key.endsWith('Field'))) shown.add(value);
      if (typeof part['message'] === 'string') for (const name of valuesIn(part['message'])) shown.add(name);
    }
    for (const node of [...(title?.above ?? []), ...(title?.before ?? []), ...(title?.after ?? []), ...(title?.below ?? [])]) shown.add(node.field);
    // The file the attachment beside the sheet shows.
    if (root.attachmentPreview?.field) shown.add(root.attachmentPreview.field);
    // A record head's kind, number, "Updated by" and toggles (its parts' own fields are among the sheet's parts above).
    for (const name of headFields(root)) shown.add(name);
  }
  // A tab's count.
  for (const name of tabCountFields(page)) shown.add(name);
  // A list names its fields in its columns, order, search, filters and groupings.
  if (root.type === 'list') {
    const read = (items: readonly FilterItem[]): string[] => items.flatMap((i) => ('any' in i ? read(i.any) : 'all' in i ? read(i.all) : [i.field]));
    for (const name of [...root.columns, ...(root.sort ?? []).map((s) => s.field), ...(root.searchFields ?? []), ...(root.groupBy ?? []), ...(root.filters ?? []).flatMap((f) => [...read(f.filter ?? []), ...(f.period ? [f.period.field] : [])])]) shown.add(name);
    if (root.map) for (const name of listMapFields(root.map)) shown.add(name);
    if (root.calendar) for (const name of listCalendarFields(root.calendar)) shown.add(name);
    if (root.gantt) for (const name of listGanttFields(root.gantt)) shown.add(name);
    if (root.card) for (const name of listCardFields(root.card)) shown.add(name);
    // And in what its rows' own tones, bold and buttons read.
    for (const when of [...(root.rowTones ?? []).map((tone) => tone.when), root.rowBold, ...(root.rowButtons ?? []).map((button) => button.invisible)]) {
      if (typeof when !== 'string') continue;
      try {
        for (const name of compileModifier(when, ANY_FUNCTION).fields) shown.add(name);
      } catch {
        // A condition that does not read is the page's checks' to say.
      }
    }
    // And in its cells' icons, its filter bar and the link its rows nest by.
    for (const name of [...Object.values(root.cells ?? {}).flatMap((cell) => (cell.lead ? [cell.lead.field] : [])), ...(root.filterFields ?? []), ...(root.parentField ? [root.parentField] : [])]) shown.add(name);
    // And in the fields its Edit offers.
    if (Array.isArray(root.bulkEdit)) for (const name of root.bulkEdit) shown.add(name);
    // And in the fields its Import offers.
    if (Array.isArray(root.import)) for (const name of root.import) shown.add(name);
    // And in the table of lines its rows open to, and the rank they are moved by.
    if (root.detail?.lines) shown.add(root.detail.lines);
    if (root.sequenceField) shown.add(root.sequenceField);
  }
  return shown;
}

/**
 * The fields a page keeps: those it shows, what a shown amount reads — its currency's field, its money look's
 * conditions' fields — and the hidden fields its link fills (`prefill`), placed nowhere.
 */
export function keptFields(page: Page): Set<string> {
  const kept = shownFields(page);
  for (const name of [...kept]) for (const read of fieldReads(page.fields[name])) kept.add(read);
  for (const one of page.prefill?.fromUrl ?? []) kept.add(typeof one === 'string' ? one : one.field);
  return kept;
}

/** The fields a field's own words read: an amount's currency's field, and those its money look's conditions read ("/month" on a rent). */
export function fieldReads(def: Field | undefined): string[] {
  if (def?.type !== 'monetary') return [];
  const reads = def.currencyField && !def.currencyField.startsWith('parent.') ? [def.currencyField] : [];
  for (const item of def.money?.per ?? []) {
    if (item.when === undefined) continue;
    try {
      reads.push(...compileModifier(item.when, ANY_FUNCTION).fields);
    } catch {
      // A condition that does not read is the page's checks' to say.
    }
  }
  return reads;
}

/** The record's fields a form's map names: each layer's place, pin and card, a layer's lines, where a route's numbers are written, the field an area is chosen into, and the areas its fences watch. */
export function mapNodeFields(node: MapNode): string[] {
  return [
    ...new Set([
      ...node.layers.flatMap((layer) => {
        // A layer of lines names its line's fields inside the field of lines: that field is the record's.
        if (layer.lines) return [layer.lines];
        const write = layer.route?.write;
        return [...listMapFields(layer), write?.seconds, write?.metres, write?.line, layer.regions?.choose].filter((name): name is string => !!name);
      }),
      ...(node.fences ?? []).flatMap((fence) => (fence.area ? [fence.area] : [])),
    ]),
  ];
}

/** The fields a list's card names: its title, the words under it, its picture, its status and its lines. */
export function listCardFields(card: ListCard): string[] {
  return [card.title, card.subtitle, card.image, card.status, ...cardLineFields(card)].filter((name): name is string => !!name);
}

/** The fields a list's calendar names: when each record starts and ends, its words, its colour and its column. */
export function listCalendarFields(calendar: ListCalendar): string[] {
  return [calendar.start, calendar.end, calendar.title, calendar.colour, calendar.resource].filter((name): name is string => !!name);
}

/** The fields a list's timeline names: when each record starts and ends or how long it lasts, its words, its group, its colour and what it waits for. */
export function listGanttFields(gantt: ListGantt): string[] {
  return [gantt.start, gantt.end, gantt.duration, gantt.title, gantt.group, gantt.colour, gantt.dependsOn].filter((name): name is string => !!name);
}

/** The fields a list's map names: where each record is, what its pin and its card show and link to, what its pin's tones and money look read, its areas' field and its heat's weight. */
export function listMapFields(map: Pick<ListMap, 'point' | 'latitude' | 'longitude' | 'pin' | 'card'> & { regions?: { from: string; field?: string }; heat?: ListMap['heat'] }): string[] {
  const pin = map.pin ?? {};
  const read = (when: string | boolean) => {
    try {
      return [...compileModifier(when, ANY_FUNCTION).fields];
    } catch {
      return [];
    }
  };
  const named = [
    map.point,
    map.latitude,
    map.longitude,
    pin.label,
    pin.iconField,
    pin.image,
    pin.turn,
    ...(pin.tones ?? []).flatMap((t) => read(t.when)),
    ...(pin.money?.per ?? []).flatMap((p) => (p.when === undefined ? [] : read(p.when))),
    map.card?.title,
    map.card?.image,
    map.card?.href,
    ...cardLineFields(map.card),
    map.regions?.field,
    typeof map.heat === 'object' ? map.heat.weight : undefined,
  ];
  return [...new Set(named.filter((name): name is string => !!name))];
}

/** Every section, wherever it sits: at the top, in a section, or in a tab. */
export function allSections(page: Page): SectionNode[] {
  return containers(page).filter((c): c is SectionNode => (c as SectionNode).type === 'section');
}

/** A field node in a section, and that section. */
export function findField(page: Page, id: string): { node: FieldNode; section: SectionNode } | null {
  for (const section of allSections(page)) {
    const node = section.children.find((n): n is FieldNode => n.type === 'field' && n.id === id);
    if (node) return { node, section };
  }
  return null;
}

/** A section's name as a list of them shows it: the tab it is in first, when it is in one. */
export function sectionLabel(page: Page, section: SectionNode, words: DesignerWords = en): string {
  const own = section.title || words.parts.untitledSection;
  const tab = tabsNodes(page)
    .flatMap((tabs) => tabs.children)
    .find((t) => t.children.includes(section));
  return tab ? words.parts.inTab(tab.label, own) : own;
}

/** Whether `id` is the tab, or something inside it. */
export function tabHolds(tab: TabNode, id: string | null): boolean {
  if (!id) return false;
  if (tab.id === id) return true;
  const walk = (nodes: LayoutNode[]): boolean => nodes.some((n) => n.id === id || ('children' in n && Array.isArray(n.children) && walk(n.children as LayoutNode[])));
  return walk(tab.children);
}

/** The ids of a menu's items at any depth: those of the menus its items open too. */
export const menuIds = (items: readonly MenuItem[] | undefined): string[] => (items ?? []).flatMap((item) => [item.id, ...menuIds(item.menu)]);

/** A sheet's own parts outside its sections, each with an id: buttons, stat buttons, badges, alerts and their buttons, ribbons. */
export function sheetParts(root: SheetNode): { id: string }[] {
  const alerts = root.alerts ?? [];
  return [...(root.buttons ?? []), ...(root.statButtons ?? []), ...(root.badges ?? []), ...alerts, ...alerts.flatMap((a) => a.buttons ?? []), ...(root.ribbon ? [root.ribbon] : []), ...(root.ribbons ?? []), ...(root.toolbar?.menu ?? []), ...headParts(root)];
}
